import { useMemo } from "react";

import { type Collection } from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import getCollectionSortOptionsQuery from "@graphql/get-collection-sort-options.graphql";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { createDefaultCollectionSortOptions } from "@react/utilities/collectionFilters";
import { STOREFRONT_API_URL } from "@tablescape/constants";
import {
  type SortOption,
  type CollectionSortOption,
  type CollectionSectionSettings,
} from "@tablescape/state";

type CollectionSortOptionsConnection = {
  data: {
    collection: {
      sortOptions: {
        references: {
          nodes: CollectionSortOption[];
        };
      };
    };
  };
};

export function useCollectionSortOptions(
  collectionHandle: Collection["handle"] | undefined
) {
  const {
    meta: {
      localization: { country },
    },
    text: {
      bestsellersLabel,
      featuredLabel,
      newestLabel,
      priceHighToLowLabel,
      priceLowToHighLabel,
    },
  } = useSectionSettings<CollectionSectionSettings>();

  const { data } = useSWR<CollectionSortOptionsConnection>(
    collectionHandle && country
      ? [
          STOREFRONT_API_URL,
          getCollectionSortOptionsQuery,
          { collectionHandle, country },
        ]
      : null,
    storefrontAPIFetcher
  );

  const sortOptions = useMemo(() => {
    if (!data) return [];

    const nodes = data?.data?.collection?.sortOptions?.references?.nodes ?? [];

    const formattedOptions = nodes
      .map<SortOption | undefined>((node) => {
        const name = node?.label?.value ?? "";
        const sortKey = node?.sortKey?.value;
        const reverseStr = node?.reverse?.value;

        if (!name || !sortKey) return undefined;

        const reverse = String(reverseStr).toLowerCase() === "true";
        return { name, value: { reverse, sortKey } };
      })
      .filter((v): v is SortOption => v !== undefined);

    return formattedOptions.length > 0
      ? formattedOptions
      : createDefaultCollectionSortOptions({
          featuredLabel,
          newestLabel,
          priceLowToHighLabel,
          priceHighToLowLabel,
          bestsellersLabel,
        });
  }, [
    data,
    featuredLabel,
    newestLabel,
    priceLowToHighLabel,
    priceHighToLowLabel,
    bestsellersLabel,
  ]);

  return {
    sortOptions,
  };
}
